import { Field, TextOutput } from 'Components/Core';
import { NamePrefixModifier, withInjectedProps } from 'Components/Core/InjectedPropsContext';
import { ListFieldSection, PageSection } from 'Components/Layout';
import { IWorksheetComponent } from 'Components/Worksheets/Core/types';
import { RemoveSeparationButton } from 'Components/Worksheets/PsdSieveAnalysisWorksheet/Components';
import {
  createSeparationCountSelector,
  getPhaseFollowingSeparation,
  isPhaseEarlierThan,
} from 'Components/Worksheets/PsdSieveAnalysisWorksheet/Helpers';
import { Phase, StateOfMaterial } from 'Components/Worksheets/PsdSieveAnalysisWorksheet/types';
import { SieveSizeId, createSieveSizeCaptionGetter } from 'Data/ViewModels/Equipment/ISieveSize';
import { valueOrDefault } from 'Lib/Any';
import { pipe } from 'Lib/Hooks/Selectors';
import React from 'react';
import { useSelector } from 'react-redux';
import DryingAtLaterSeparationSection from './DryingAtLaterSeparationSection';
import SeparationAssumedDrySection from './SeparationAssumedDrySection';
import SeparationOvenDryingSection from './SeparationOvenDryingSection';
import SeparationUsingRelatedWaterContentSection from './SeparationUsingRelatedWaterContentSection';

let SeparationSection: React.FC<IWorksheetComponent> = ({ createWorksheetSelector, namePrefix }) => {
  const separatingSieveSizeId: SieveSizeId = useSelector(createWorksheetSelector!('Separation.SeparatingSieveSizeId'));
  const separatingSieveSizeCaption = useSelector(
    pipe(
      createWorksheetSelector!('data.Equipment.AllSieveSizes', false),
      createSieveSizeCaptionGetter(separatingSieveSizeId),
      valueOrDefault('N/A')
    )
  );

  // Separation index
  const index = namePrefix?.match(/\.Separation\./g)?.length ?? 0;

  const separationCount = useSelector(createSeparationCountSelector(createWorksheetSelector!));
  const separationHeading = separationCount > 1 ? `Separation ${index + 1}` : 'Separation';

  const dryingPhase = useSelector(createWorksheetSelector!('data.Preparation.DryingPhase', false)) as Phase;
  const phaseFollowingSeparation = getPhaseFollowingSeparation(index);

  const isAlreadyDry = isPhaseEarlierThan(dryingPhase, phaseFollowingSeparation) || dryingPhase === Phase.Never;

  const stateOfMaterial = useSelector(
    createWorksheetSelector!('data.Preparation.TotalDryMassDeterminationBy', false)
  ) as StateOfMaterial;

  const isDriedAtCurrentSeparation = dryingPhase === phaseFollowingSeparation;

  const isDriedAfterSeparation = !isDriedAtCurrentSeparation && !isAlreadyDry;

  return (
    <PageSection heading={separationHeading}>
      <ListFieldSection>
        <Field input={<TextOutput />} name="Separation.SeparatingSieveSizeId" value={separatingSieveSizeCaption} />
      </ListFieldSection>

      {isAlreadyDry && <SeparationAssumedDrySection />}
      {isDriedAfterSeparation && <DryingAtLaterSeparationSection />}
      {isDriedAtCurrentSeparation && stateOfMaterial === StateOfMaterial.OvenDry && <SeparationOvenDryingSection />}
      {isDriedAtCurrentSeparation && stateOfMaterial === StateOfMaterial.Moist && (
        <SeparationUsingRelatedWaterContentSection />
      )}

      <NamePrefixModifier appendNamePrefix="Separation">
        <div>
          <RemoveSeparationButton />
        </div>
      </NamePrefixModifier>
    </PageSection>
  );
};

SeparationSection = withInjectedProps(SeparationSection);

export { SeparationSection };
export default SeparationSection;
